/**
 * Stencil numbers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const stencilNumbersPack: ToilePack = {
    slug: 'stencil-numbers',
    category: 'illustration',
    name: 'Stencil numbers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'digits', label: '0 to 9', subject: 'a sheet of the ten stencil digits "0" to "9" in solid black in two rows, bridges holding the centers of 0, 4, 6, 8 and 9'},
        {key: 'large', label: 'One large digit', subject: 'a single stencil digit "8" filling the page in solid black, two bridges holding both centers, a dashed cut border around it'},
        {key: 'house', label: 'House number', subject: 'the house number "142" as one stencil strip across the page in bold stencil numerals with visible bridges'},
    ],
    styles: [
        {key: 'military', label: 'Military', style: 'classic army-style stencil numerals, heavy and squared, with bridges breaking the closed shapes', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'rounded', label: 'Rounded', style: 'soft rounded stencil numerals with wide bridges, friendly and easy to cut', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'slim', label: 'Slim', style: 'tall slim stencil numerals with thin neat bridges, a clean sign-painter look', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
    ],
    directions: ['a name', 'letters', 'a date'],
    directionHint: 'try: a name',
    seo: {
        title: 'Stencil Numbers: Digits 0 to 9, Large Digit, House Number',
        metaDescription: 'Nine printable number stencils: digits 0 to 9, a large single digit and a house number, with bridges for a craft knife.',
        h1: 'Stencil numbers: digits 0 to 9, one large digit, a house number',
        tagline: 'Solid number stencils with bridges, in military, rounded and slim cuts.',
        intro: 'Number stencils are printed, cut out and painted through for house numbers, parking spots, crates and sports gear. The nine sheets show the ten stencil digits 0 to 9 in two rows with bridges holding the closed shapes, a single large 8 filling the page, and the house number 142 as one stencil strip. Each comes as a military cut, a rounded cut with wide bridges, or a slim sign-painter cut. Ask for a name, letters or a date.',
        useCases: [
            'a house number on a door or a curb',
            'a parking spot',
            'a crate or box number',
            'a jersey number stencil',
        ],
        faq: [
            {q: 'Why are there bridges?', a: 'Closed shapes such as the inside of an 8 or a 0 would fall out once cut. The small bridges hold them in place.'},
            {q: 'Is the single digit big enough to cut?', a: 'Yes. The large 8 fills the page and is set for a craft knife.'},
            {q: 'What is the difference between the three cuts?', a: 'Military is heavy and squared. Rounded has soft shapes and wide bridges. Slim is tall and thin, with neat bridges.'},
            {q: 'Which digits are shown?', a: 'All ten digits, 0 to 9, on the first sheet.'},
        ],
    },
}
